/**
 * Store abstraction for purchases. Same product ids everywhere: sketchling.<individual|family>.<monthly|yearly>.
 *
 *   revenuecat  native iOS / Android (App Store / Play Billing via @revenuecat/purchases-capacitor);
 *               app_user_id = our user id, then POST /billing/revenuecat/sync verifies server-side
 *               (webhooks keep it current). Ask to Buy → PAYMENT_PENDING → "waiting for a grown-up".
 *   stripe      web: redirect to Stripe Checkout (POST /billing/checkout), /billing/success confirms.
 *   mock        dev default (and when no store is configured): POST /billing/mock-purchase.
 *
 * Selection: native + VITE_RC_<IOS|ANDROID>_KEY → revenuecat; web + VITE_WEB_BILLING=stripe → stripe;
 * otherwise mock. The server stays the source of truth for the entitlement.
 */
import { normalizeLocale, pickLocale, type LocaleTable } from '@sketchling/core';
import { Purchases, type PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  PLAN_DISPLAY_NAME,
  TRIAL_DAYS,
  TRIAL_REMINDER_DAY,
  localizedOffers,
  productId,
  type BillingPeriod,
  type Lang,
  type OfferPackage,
  type PendingPurchase,
  type PlanId,
  type Tier,
} from '@sketchling/core';
import { api, ApiError, ensureAccount, entitlementFromServer, request, type ServerEntitlement, type ServerPending } from './api';
import { isNative, platform } from './platform';
import { useAnalytics } from './analytics';
import { useApp } from '../state/store';

export type PurchaseResult =
  | { ok: true; trial: boolean }
  | { ok: false; reason: 'cancelled' | 'blocked' | 'error' | 'pending' | 'redirect' | 'purchasesOff' };

export interface StoreAdapter {
  readonly name: 'mock' | 'revenuecat' | 'stripe';
  purchase(plan: PlanId, period: BillingPeriod): Promise<PurchaseResult>;
  /** Re-reads store state; true when a paid plan is active. */
  restore(): Promise<boolean>;
}

const env = import.meta.env;
const rcKey = (): string | undefined => (platform === 'ios' ? env.VITE_RC_IOS_KEY : platform === 'android' ? env.VITE_RC_ANDROID_KEY : undefined) || undefined;

/* ---------------------------------- Helpers ---------------------------------- */

export function applyServerEntitlement(e: ServerEntitlement, plan?: PlanId) {
  const st = useApp.getState();
  const next = entitlementFromServer(e, st.entitlement);
  st.setEntitlement({ ...next, premiumProfileId: plan === 'individual' ? (st.activeProfileId ?? undefined) : next.premiumProfileId });
  if (plan === 'individual') {
    const remote = st.profiles.find((p) => p.id === st.activeProfileId)?.remoteId;
    if (remote) void api.markPremium(remote).catch(() => undefined);
  }
}

export function applyServerPending(p: ServerPending | null | undefined) {
  useApp.getState().setPendingPurchase(p ? { plan: p.plan, period: p.period, since: p.since, store: p.store } : null);
}

function markPending(plan: PlanId, period: BillingPeriod, store: string) {
  const pending: PendingPurchase = { plan, period, since: new Date().toISOString(), store };
  useApp.getState().setPendingPurchase(pending);
  useApp.getState().logEvent('Purchase Pending', { plan, period, store });
}

/* ------------------------------------ Mock ------------------------------------ */

export const mockStore: StoreAdapter = {
  name: 'mock',
  async purchase(plan, period) {
    const st = useApp.getState();
    try {
      if (await ensureAccount()) {
        if (st.cheats.simAskToBuy) {
          const r = await api.mockAskToBuy(plan, period);
          applyServerPending(r.pending);
          return { ok: false, reason: 'pending' };
        }
        const r = await api.mockPurchase(plan, period);
        applyServerEntitlement(r.entitlement, plan);
        return { ok: true, trial: r.entitlement.isTrial };
      }
    } catch (e) {
      if (e instanceof ApiError && e.status !== 0) return { ok: false, reason: 'error' };
    }
    // Offline demo: local mock trial.
    if (!import.meta.env.DEV || !st.cheats.mockPurchase) return { ok: false, reason: 'error' };
    if (st.cheats.simAskToBuy) {
      markPending(plan, period, 'mock');
      return { ok: false, reason: 'pending' };
    }
    const until = new Date(Date.now() + 7 * 86400_000).toISOString();
    st.setEntitlement({ tier: plan, period, trialEndsAt: until, expiresAt: until, source: 'local-mock', premiumProfileId: plan === 'individual' ? (st.activeProfileId ?? undefined) : undefined });
    return { ok: true, trial: true };
  },
  async restore() {
    if (!useApp.getState().account.accessToken) return false;
    const r = await api.restore();
    applyServerEntitlement(r.entitlement);
    applyServerPending(r.pending);
    return r.entitlement.tier !== 'free';
  },
};

/* --------------------------------- RevenueCat --------------------------------- */

let rcUser: string | null = null;

async function ensureRevenueCat(): Promise<boolean> {
  const key = rcKey();
  if (!key || !(await ensureAccount())) return false;
  const userId = useApp.getState().account.userId;
  if (!userId) return false;
  if (rcUser === null) await Purchases.configure({ apiKey: key, appUserID: userId });
  else if (rcUser !== userId) await Purchases.logIn({ appUserID: userId });
  rcUser = userId;
  return true;
}

async function findPackage(id: string): Promise<PurchasesPackage | null> {
  const offerings = await Purchases.getOfferings();
  // Invited families buy from the 14-day-trial offering (store intro offer), everyone else from current.
  const invited = useMoney.getState().invitedTrialDays ? offerings.all?.invited_14d : undefined;
  const all = [invited, offerings.current, ...Object.values(offerings.all ?? {})].filter(Boolean);
  for (const o of all) {
    // Android products look like "sketchling.family.yearly:yearly-base".
    const pkg = o!.availablePackages.find((p) => p.product.identifier === id || p.product.identifier.startsWith(`${id}:`));
    if (pkg) return pkg;
  }
  return null;
}

async function syncFromRevenueCat(plan?: PlanId): Promise<ServerEntitlement | null> {
  try {
    const r = await api.revenuecatSync();
    applyServerEntitlement(r.entitlement, plan);
    return r.entitlement;
  } catch {
    // Sync not configured: the webhook will update the account; read what the server has now.
    const r = await api.restore().catch(() => null);
    if (r) applyServerEntitlement(r.entitlement, plan);
    return r?.entitlement ?? null;
  }
}

export const revenueCatStore: StoreAdapter = {
  name: 'revenuecat',
  async purchase(plan, period) {
    try {
      if (!(await ensureRevenueCat())) return { ok: false, reason: 'error' };
      const id = productId(plan, period);
      const pkg = await findPackage(id);
      if (pkg) await Purchases.purchasePackage({ aPackage: pkg });
      else {
        const { products } = await Purchases.getProducts({ productIdentifiers: [id] });
        if (!products[0]) return { ok: false, reason: 'error' };
        await Purchases.purchaseStoreProduct({ product: products[0] });
      }
      const e = await syncFromRevenueCat(plan);
      return { ok: true, trial: !!e?.isTrial };
    } catch (err) {
      const code = String((err as { code?: string | number }).code ?? '');
      if (code === '1' || (err as { userCancelled?: boolean }).userCancelled) return { ok: false, reason: 'cancelled' };
      if (code === '20') {
        // Ask to Buy (iOS) / pending transaction (Play): access arrives later via webhook.
        markPending(plan, period, platform === 'ios' ? 'app_store' : 'play_store');
        return { ok: false, reason: 'pending' };
      }
      if (code === '3') return { ok: false, reason: 'purchasesOff' };
      return { ok: false, reason: 'error' };
    }
  },
  async restore() {
    if (!(await ensureRevenueCat())) return false;
    await Purchases.restorePurchases();
    const e = await syncFromRevenueCat();
    return !!e && e.tier !== 'free';
  },
};

/* ----------------------------------- Stripe ----------------------------------- */

export const stripeStore: StoreAdapter = {
  name: 'stripe',
  async purchase(plan, period) {
    try {
      if (!(await ensureAccount())) return { ok: false, reason: 'error' };
      const { url } = await api.checkout(plan, period);
      useApp.getState().logEvent('Checkout Redirect', { plan, period });
      window.location.assign(url);
      return { ok: false, reason: 'redirect' };
    } catch {
      return { ok: false, reason: 'error' };
    }
  },
  restore: () => mockStore.restore(),
};

/** The adapter for this platform / build. */
export function currentStore(): StoreAdapter {
  if (isNative && rcKey()) return revenueCatStore;
  if (!isNative && env.VITE_WEB_BILLING === 'stripe') return stripeStore;
  return mockStore;
}

/* ============================================================================================
 * Monetization P0 (docs/MONETIZATION-PLAN.md, scratch MONEY-API contract): store-localized offerings,
 * subscription status / notices, referrals ("Invite a family"), gifts, and the per-device money state
 * (pending invite code, invited trial, book style choice).
 * ============================================================================================ */

export type OfferSource = 'store' | 'server' | 'table';

export interface Offerings {
  /** store = RevenueCat SDK (App Store / Play price strings); server = GET /billing/offerings; table = offline estimate. */
  source: OfferSource;
  provider: string;
  offeringId: string;
  country: string;
  currency: string;
  trialReminderDay: number;
  packages: OfferPackage[];
}

type ServerOfferings = Omit<Offerings, 'source'>;

export interface BillingStatus {
  tier: Tier;
  period: BillingPeriod | null;
  status: 'trialing' | 'active' | 'grace' | 'billing_issue' | 'paused' | 'canceled' | 'expired' | 'pending' | 'none';
  source: string | null;
  willRenew: boolean;
  trialEndsAt: string | null;
  expiresAt: string | null;
  graceEndsAt: string | null;
  familyShared: boolean;
  managementUrl: string | null;
  queuedGifts: number;
  history?: { at: string; type: string; detail?: unknown }[];
}

export type NoticeKind = 'trial_day5' | 'billing_issue' | 'gift_queued' | 'referral_reward' | 'refund';
export interface BillingNotice {
  id: string;
  kind: NoticeKind | string;
  at: string;
  data?: Record<string, unknown> | null;
}

export interface ReferralMe {
  code: string;
  link: string;
  stats: { claimed: number; paid: number; rewardedMonths: number; cap: number };
}

export type GiftProduct = 'family_yearly' | 'plus_yearly';
export interface GiftCheckout {
  url?: string;
  mock?: boolean;
  code?: string;
  redeemUrl?: string;
}

export const moneyApi = {
  offerings: (q: { country: string; lang: string; placement: string }, auth = true) =>
    request<ServerOfferings>(`/billing/offerings?${new URLSearchParams({ ...q, platform }).toString()}`, { auth }),
  status: () => request<BillingStatus>('/billing/status'),
  notices: () => request<{ notices: BillingNotice[] }>('/billing/notices'),
  dismissNotice: (id: string) => request<unknown>(`/billing/notices/${encodeURIComponent(id)}/dismiss`, { method: 'POST', body: JSON.stringify({}) }),
  referralMe: (deviceId?: string) => request<ReferralMe>(`/referrals/me${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ''}`),
  referralInfo: (code: string) => request<{ valid: boolean; trialDays: number }>(`/referrals/${encodeURIComponent(code)}`, { auth: false }),
  referralClaim: (code: string, deviceId?: string) => request<{ ok: boolean; trialDays: number; offeringId: string }>('/referrals/claim', { method: 'POST', body: JSON.stringify({ code, deviceId }) }),
  giftCheckout: (product: GiftProduct, shareId?: string, lang?: string, country?: string) =>
    request<GiftCheckout>('/gifts/checkout', { method: 'POST', auth: false, body: JSON.stringify({ product, shareId, lang, country }) }),
  giftSession: (sessionId: string) => request<{ status: string; product: GiftProduct; code?: string; redeemUrl?: string; expiresAt?: string }>(`/gifts/session/${encodeURIComponent(sessionId)}`, { auth: false }),
  portal: () => request<{ url: string }>('/billing/portal', { method: 'POST', body: JSON.stringify({}) }),
  giftInfo: (code: string) => request<{ valid: boolean; product: GiftProduct; redeemed: boolean; expiresAt: string | null }>(`/gifts/${encodeURIComponent(code)}`, { auth: false }),
  giftRedeem: (code: string) => request<{ ok: boolean; queued: boolean; entitlement: ServerEntitlement }>('/gifts/redeem', { method: 'POST', body: JSON.stringify({ code }) }),
};

/** Public web pages (legal, store management). Overridable per build. */
/** Public legal pages (static HTML on the web app's origin: apps/client/public/{privacy,terms,delete-account}.html). */
const WEB_ORIGIN = (env.VITE_WEB_ORIGIN as string | undefined) || 'https://sketchling-web.pages.dev';
export const TERMS_URL = (env.VITE_TERMS_URL as string | undefined) || `${WEB_ORIGIN}/terms`;
export const PRIVACY_URL = (env.VITE_PRIVACY_URL as string | undefined) || `${WEB_ORIGIN}/privacy`;
export const DELETE_ACCOUNT_URL = `${WEB_ORIGIN}/delete-account`;
const STORE_MANAGE_URL = {
  ios: 'https://apps.apple.com/account/subscriptions',
  android: 'https://play.google.com/store/account/subscriptions?package=app.sketchling.draw',
} as const;

/* ------------------------------- per-device money state ------------------------------- */

export type SkinName = 'storybook' | 'sticker' | 'pastel';

interface MoneyState {
  /** Invite code opened via /i/:code, claimed once an account exists. */
  pendingInvite: string | null;
  /** Trial length granted by a claimed invite (yearly packages), null = not invited. */
  invitedTrialDays: number | null;
  /** Book style picked in Settings (applies for subscribers only). */
  skin: SkinName | null;
}

const moneyStorage = createJSONStorage(() => ({
  getItem: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* ignore */
    }
  },
  removeItem: (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
}));

export const useMoney = create<MoneyState>()(persist(() => ({ pendingInvite: null as string | null, invitedTrialDays: null as number | null, skin: null as SkinName | null }), { name: 'sketchling-money-v1', storage: moneyStorage }));

/* ------------------------------------- offerings ------------------------------------- */

/** Store country: `?country=XX` (dev/testing, remembered for the tab) → the device locale region → language. */
export function deviceCountry(lang: Lang): string {
  try {
    const q = new URLSearchParams(location.search).get('country');
    if (q && /^[A-Za-z]{2}$/.test(q)) sessionStorage.setItem('sk-country', q.toUpperCase());
    const saved = sessionStorage.getItem('sk-country');
    if (saved) return saved;
  } catch {
    /* ignore */
  }
  const langs = typeof navigator !== 'undefined' ? [...(navigator.languages ?? []), navigator.language] : [];
  for (const l of langs) {
    const m = /^[a-z]{2,3}[-_]([A-Z]{2})\b/i.exec(l ?? '');
    if (m) return m[1]!.toUpperCase();
  }
  return pickLocale(DEFAULT_COUNTRY, normalizeLocale(lang));
}

/** Storefront country guess when the device gives no region. */
const DEFAULT_COUNTRY: LocaleTable<string> = { en: 'US', vi: 'VN', 'zh-Hans': 'CN' };

const PERIOD_DAYS: Record<string, number> = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };

function parseProduct(identifier: string): { plan: PlanId; period: BillingPeriod } | null {
  const m = /^sketchling\.(individual|family)\.(monthly|yearly)/.exec(identifier);
  return m ? { plan: m[1] as PlanId, period: m[2] as BillingPeriod } : null;
}

/** RevenueCat SDK offerings → packages with the store's own price strings. */
async function storeOfferings(placement: string, country: string): Promise<Offerings | null> {
  if (!(isNative && rcKey()) || !(await ensureRevenueCat())) return null;
  const all = await Purchases.getOfferings();
  const invited = !!useMoney.getState().invitedTrialDays;
  const pick = (invited && all.all?.invited_14d) || (placement === 'onboarding_alive' && all.all?.onboarding) || all.current;
  if (!pick) return null;
  const monthly: Partial<Record<PlanId, number>> = {};
  const raw = pick.availablePackages
    .map((p) => ({ p, id: parseProduct(p.product.identifier) }))
    .filter((x): x is { p: PurchasesPackage; id: { plan: PlanId; period: BillingPeriod } } => !!x.id);
  for (const { p, id } of raw) if (id.period === 'monthly') monthly[id.plan] = p.product.price;
  const packages: OfferPackage[] = raw.map(({ p, id }) => {
    const pr = p.product;
    const intro = pr.introPrice;
    const trialDays = intro && intro.price === 0 ? intro.periodNumberOfUnits * (PERIOD_DAYS[intro.periodUnit] ?? 1) : 0;
    const m = monthly[id.plan];
    return {
      plan: id.plan,
      period: id.period,
      productId: productId(id.plan, id.period),
      displayName: PLAN_DISPLAY_NAME[id.plan],
      priceString: pr.priceString,
      price: pr.price,
      currency: pr.currencyCode,
      pricePerMonthString: pr.pricePerMonthString ?? pr.priceString,
      discountPercent: id.period === 'yearly' && m ? Math.max(0, Math.floor((1 - pr.price / (m * 12)) * 100)) : 0,
      // Decision 2026-10-18: a trial is only ever shown on yearly packages.
      trial: id.period === 'yearly' && trialDays > 0 ? { days: trialDays } : null,
    };
  });
  if (!packages.length) return null;
  return { source: 'store', provider: 'revenuecat', offeringId: pick.identifier, country, currency: packages[0]!.currency, trialReminderDay: TRIAL_REMINDER_DAY, packages };
}

const offeringsCache = new Map<string, { at: number; value: Offerings }>();

/**
 * Prices for the paywall / gift page, never hard-coded USD: native store (RevenueCat) → API
 * (Stripe prices on the web, regional mock table in dev) → the core regional table (offline estimate,
 * marked `source: 'table'`).
 */
export async function loadOfferings(placement: string, lang: Lang): Promise<Offerings> {
  const country = deviceCountry(lang);
  const st = useApp.getState();
  const invitedDays = useMoney.getState().invitedTrialDays;
  const key = `${placement}|${lang}|${country}|${st.account.userId ?? '-'}|${invitedDays ?? 0}`;
  const hit = offeringsCache.get(key);
  if (hit && Date.now() - hit.at < 5 * 60_000) return hit.value;
  let value: Offerings | null = null;
  try {
    value = await storeOfferings(placement, country);
  } catch {
    value = null;
  }
  if (!value) {
    try {
      const r = await moneyApi.offerings({ country, lang, placement }).catch((e) => {
        // A stale session must not cost the parent the real prices: the endpoint also works signed out.
        if (e instanceof ApiError && e.status !== 0) return moneyApi.offerings({ country, lang, placement }, false);
        throw e;
      });
      if (r?.packages?.length) value = { ...r, source: 'server' };
    } catch {
      value = null;
    }
  }
  if (!value) {
    const trialDays = invitedDays ?? st.config.trialDays ?? TRIAL_DAYS;
    const o = localizedOffers(country, lang, { trialDays });
    value = { source: 'table', provider: 'table', offeringId: invitedDays ? 'invited_14d' : 'default', country: o.country, currency: o.currency, trialReminderDay: TRIAL_REMINDER_DAY, packages: o.packages };
  } else offeringsCache.set(key, { at: Date.now(), value });
  return value;
}

export function clearOfferingsCache(): void {
  offeringsCache.clear();
}

/* ---------------------------------- status / manage ---------------------------------- */

/** Subscription status for "Manage plan"; falls back to the local entitlement when offline / older API. */
export async function loadBillingStatus(): Promise<BillingStatus> {
  const st = useApp.getState();
  if (st.account.accessToken) {
    try {
      return await moneyApi.status();
    } catch {
      /* fall through */
    }
  }
  const e = st.entitlement;
  const now = Date.now();
  const trialing = !!e.trialEndsAt && Date.parse(e.trialEndsAt) > now;
  return {
    tier: e.tier,
    period: e.period ?? null,
    status: e.tier === 'free' ? 'none' : trialing ? 'trialing' : 'active',
    source: e.source ?? null,
    willRenew: e.tier !== 'free',
    trialEndsAt: e.trialEndsAt ?? null,
    expiresAt: e.expiresAt ?? null,
    graceEndsAt: null,
    familyShared: false,
    managementUrl: null,
    queuedGifts: 0,
  };
}

/** Where "Cancel / manage subscription" goes: the store's own page (native), Stripe portal (web) or null. */
export async function managementUrl(status?: BillingStatus | null): Promise<string | null> {
  if (status?.managementUrl) return status.managementUrl;
  // Web (Stripe): the Customer Portal session (503 without Stripe → no link).
  if (!isNative && status?.source === 'stripe') {
    try {
      return (await moneyApi.portal()).url;
    } catch {
      return null;
    }
  }
  if (isNative && rcKey()) {
    try {
      if (await ensureRevenueCat()) {
        const { customerInfo } = await Purchases.getCustomerInfo();
        if (customerInfo.managementURL) return customerInfo.managementURL;
      }
    } catch {
      /* fall back to the store page */
    }
  }
  if (platform === 'ios') return STORE_MANAGE_URL.ios;
  if (platform === 'android') return STORE_MANAGE_URL.android;
  return null;
}

/* ------------------------------------- referrals ------------------------------------- */

export type ClaimResult = 'claimed' | 'none' | 'offline' | 'invalid_code' | 'self_referral' | 'already_claimed' | 'already_subscribed' | 'limit' | 'error';

/** Remember an invite code from /i/:code until an account exists. */
export function rememberInvite(code: string): void {
  useMoney.setState({ pendingInvite: code.trim().toUpperCase() });
}

/** Claim the pending invite (creates the guest account if needed). Idempotent; safe to call often. */
export async function claimPendingInvite(): Promise<ClaimResult> {
  const code = useMoney.getState().pendingInvite;
  if (!code) return 'none';
  if (!(await ensureAccount())) return 'offline';
  try {
    const r = await moneyApi.referralClaim(code, useAnalytics.getState().installId);
    useMoney.setState({ pendingInvite: null, invitedTrialDays: r.trialDays || useApp.getState().config.referralTrialDays || 14 });
    clearOfferingsCache();
    return 'claimed';
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) return 'offline';
    if (e instanceof ApiError && e.status === 404) return 'error'; // API not deployed yet: keep the code for later
    const code2 = e instanceof ApiError ? e.code : 'error';
    useMoney.setState({ pendingInvite: null });
    return (['invalid_code', 'self_referral', 'already_claimed', 'already_subscribed', 'limit'].includes(code2) ? code2 : 'error') as ClaimResult;
  }
}

/* --------------------------------------- gifts --------------------------------------- */

export type RedeemResult = { ok: true; queued: boolean } | { ok: false; reason: 'invalid_code' | 'already_redeemed' | 'expired' | 'offline' | 'error' };

export async function redeemGift(code: string): Promise<RedeemResult> {
  if (!(await ensureAccount())) return { ok: false, reason: 'offline' };
  try {
    const r = await moneyApi.giftRedeem(code.trim().toUpperCase());
    applyServerEntitlement(r.entitlement);
    clearOfferingsCache();
    return { ok: true, queued: !!r.queued };
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) return { ok: false, reason: 'offline' };
    const c = e instanceof ApiError ? e.code : 'error';
    return { ok: false, reason: (['invalid_code', 'already_redeemed', 'expired'].includes(c) ? c : 'error') as 'error' };
  }
}

/** Gift codes are web/Android only: never an in-app code box on iOS (App Review 3.1.1). */
export const canRedeemInApp = () => platform !== 'ios';

const GIFT_KEY = 'sk-gift-code';
/** A gift code from a redeem link opened before sign-in (pre-fills Grown-ups corner → Redeem). */
export function rememberGiftCode(code: string | null): void {
  try {
    if (code) localStorage.setItem(GIFT_KEY, code);
    else localStorage.removeItem(GIFT_KEY);
  } catch {
    /* ignore */
  }
}
export function pendingGiftCode(): string {
  try {
    return localStorage.getItem(GIFT_KEY) ?? '';
  } catch {
    return '';
  }
}
